// ---------------------------------------------------------------------------
// Pre-cache assets — fetches index HTML, discovers static asset URLs,
// then fetches them in batches so the SW's cacheFirst handler stores them.
// ---------------------------------------------------------------------------

export interface PrecacheProgress {
  total: number;
  loaded: number;
  phase: 'discovering' | 'fetching' | 'complete' | 'error';
}

type ProgressCallback = (progress: PrecacheProgress) => void;

const BATCH_SIZE = 6;
const ASSET_PATTERN = /\/_next\/static\/[^\s"'<>]+/g;

function extractAssetUrls(html: string): string[] {
  const matches = html.match(ASSET_PATTERN);
  if (!matches) return [];
  const unique = new Set(matches.map((url) => url.split('?')[0]));
  return Array.from(unique);
}

async function fetchBatch(urls: string[]): Promise<number> {
  const results = await Promise.allSettled(
    urls.map((url) =>
      fetch(url, { cache: 'reload', credentials: 'same-origin' })
    )
  );
  return results.filter((r) => r.status === 'fulfilled').length;
}

export async function precacheAssets(
  onProgress: ProgressCallback
): Promise<void> {
  try {
    onProgress({ total: 0, loaded: 0, phase: 'discovering' });

    const res = await fetch('/', {
      cache: 'reload',
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    });
    if (!res.ok) {
      onProgress({ total: 0, loaded: 0, phase: 'error' });
      return;
    }
    const html = await res.text();
    const urls = extractAssetUrls(html);
    const total = urls.length;

    if (total === 0) {
      onProgress({ total: 0, loaded: 0, phase: 'complete' });
      return;
    }

    let loaded = 0;
    onProgress({ total, loaded, phase: 'fetching' });

    for (let i = 0; i < urls.length; i += BATCH_SIZE) {
      const batch = urls.slice(i, i + BATCH_SIZE);
      const fetched = await fetchBatch(batch);
      loaded += fetched;
      onProgress({ total, loaded, phase: 'fetching' });
    }

    onProgress({ total, loaded, phase: 'complete' });
  } catch {
    onProgress({ total: 0, loaded: 0, phase: 'error' });
  }
}
